/**
 * Utilities to make Recharts donut charts robust:
 * - Preserve a stable category order (optional)
 * - Ensure missing categories show as 0
 * - Render 0-valued categories as tiny visible slices (epsilon) while displaying real 0 in UI
 */

/**
 * @typedef {{ name: string, value: number, color?: string }} PieDatum
 * @typedef {{ name: string, value: number, renderValue: number, color?: string }} PieRenderDatum
 */

const DEFAULT_EPSILON_MIN = 0.05;
const DEFAULT_EPSILON_RATIO = 0.0005; // 0.05% of total
const DEFAULT_EPSILON_MAX_OF_MIN_POSITIVE = 0.25;

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function keyOfName(name) {
  return String(name || '').trim();
}

/**
 * Ensures desired categories exist (fill missing with 0) and keeps unknown categories appended.
 * @param {PieDatum[]} data
 * @param {{name: string, color?: string}[]} desired
 * @returns {PieDatum[]}
 */
export function ensureCategories(data, desired) {
  const byName = new Map();
  (Array.isArray(data) ? data : []).forEach((d) => {
    const name = keyOfName(d?.name);
    if (!name) return;
    byName.set(name, { ...d, name, value: toNumber(d?.value) });
  });

  const result = [];
  const used = new Set();

  (Array.isArray(desired) ? desired : []).forEach((cat) => {
    const name = keyOfName(cat?.name);
    if (!name) return;
    const existing = byName.get(name);
    result.push({
      name,
      value: existing ? toNumber(existing.value) : 0,
      color: existing?.color ?? cat?.color,
    });
    used.add(name);
  });

  // Append any extra categories not in desired list (keep their API order).
  (Array.isArray(data) ? data : []).forEach((d) => {
    const name = keyOfName(d?.name);
    if (!name || used.has(name)) return;
    result.push({ ...d, name, value: toNumber(d?.value) });
  });

  return result;
}

/**
 * Adds `renderValue` for Recharts rendering:
 * - value>0 => renderValue=value
 * - value===0:
 *   - if any positive exists => renderValue=epsilon (tiny slice)
 *   - if all values are 0 => renderValue=1 for all (so donut shows all categories)
 *
 * Note: Tooltip/legend should use `value` (real), not `renderValue`.
 *
 * @param {PieDatum[]} data
 * @param {{ epsilonMin?: number, epsilonRatio?: number, epsilonMaxOfMinPositive?: number }} opts
 * @returns {{ data: PieRenderDatum[], total: number, positiveTotal: number }}
 */
export function withRenderValue(data, opts = {}) {
  const epsilonMin = toNumber(opts.epsilonMin ?? DEFAULT_EPSILON_MIN);
  const epsilonRatio = toNumber(opts.epsilonRatio ?? DEFAULT_EPSILON_RATIO);
  const epsilonMaxOfMinPositive = toNumber(
    opts.epsilonMaxOfMinPositive ?? DEFAULT_EPSILON_MAX_OF_MIN_POSITIVE
  );

  const normalized = (Array.isArray(data) ? data : []).map((d) => ({
    ...d,
    name: keyOfName(d?.name),
    value: toNumber(d?.value),
  }));

  const total = normalized.reduce((s, d) => s + (d.value > 0 ? d.value : 0), 0);
  const positives = normalized.filter((d) => d.value > 0).map((d) => d.value);
  const minPositive = positives.length ? Math.min(...positives) : 0;

  if (positives.length === 0) {
    return {
      total: 0,
      positiveTotal: 0,
      data: normalized.map((d) => ({
        ...d,
        renderValue: 1,
      })),
    };
  }

  let epsilon = Math.max(total * epsilonRatio, epsilonMin);
  if (minPositive > 0) {
    epsilon = Math.min(epsilon, minPositive * epsilonMaxOfMinPositive);
  }

  return {
    total: normalized.reduce((s, d) => s + d.value, 0),
    positiveTotal: total,
    data: normalized.map((d) => ({
      ...d,
      renderValue: d.value > 0 ? d.value : epsilon,
    })),
  };
}

